import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Lock, CheckCircle2 } from "lucide-react";

/**
 * Modal that confirms event payment and POSTs to /api/events/{id}/pay.
 *
 * Props:
 *   event         - the event we're paying for. Must have `id`, `title`,
 *                   `price_cents > 0`.
 *   open          - controlled open flag.
 *   onOpenChange  - dialog close handler.
 *   onPaid        - called with the server response on a successful capture.
 *   buyer         - optional {name, email} pre-fill.
 */
export function EventPaymentDialog({ event, open, onOpenChange, onPaid, buyer }) {
  const [processing, setProcessing] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const priceCents = Number(event?.price_cents || 0);
  const amountLabel = `$${(priceCents / 100).toFixed(2)}`;

  const submitPayment = async () => {
    if (!event?.id || processing) return;
    setProcessing(true);
    try {
      const { data } = await api.post(`/events/${event.id}/pay`, {
        source_id: "direct",
        buyer_name: buyer?.name || undefined,
        buyer_email: buyer?.email || undefined,
      });
      setReceipt(data);
      onPaid && onPaid(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Payment failed";
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const dismiss = () => {
    setReceipt(null);
    onOpenChange && onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent
        className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="event-pay-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-[hsl(var(--accent))]" />
            {receipt ? "Payment received" : `Pay ${amountLabel} for ${event?.title || "event"}`}
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            {receipt
              ? "Thanks - your attendance is confirmed."
              : "Confirm your payment for this event."}
          </DialogDescription>
        </DialogHeader>

        {receipt ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-300 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold text-emerald-700 dark:text-emerald-300">
                  Paid {amountLabel}
                </div>
              </div>
            </div>
            <Button
              type="button"
              className="w-full h-11 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
              onClick={dismiss}
              data-testid="event-pay-done"
            >
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
              You are about to confirm payment of <span className="font-semibold text-foreground">{amountLabel}</span> for{" "}
              <span className="font-semibold text-foreground">{event?.title || "this event"}</span>.
            </div>
            <Button
              type="button"
              className="w-full h-11 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
              disabled={processing}
              onClick={submitPayment}
              data-testid="event-pay-confirm-btn"
            >
              {processing ? "Processing..." : `Confirm Payment ${amountLabel}`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
